import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import Loader from "layout/Loader";
import BackendPaginationTable from "common/Table/TableWithPagination";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import { useNavigate } from "react-router-dom";
import { Grid, Typography } from "@mui/material";
import StyledInput from "common/SearchField/Search";
import { useGetExpenseTypeService } from "subModule/src/services/expense/useExpense";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { ExpenseType } from "subModule/src/services/expense/interface";
import FormDialog from "common/Modal/Modal";
import { ExpenseProfileService } from "subModule/src/core/services";
import { DeleteData } from "subModule/src/type";

export default function ExpenseTypeListing() {
  const navigate = useNavigate();
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<any>("");
  const [search, setSearch] = useState("");
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [row, setRow] = useState<ExpenseType>();
  const {
    data: tableData,
    isFetching,
    refetch,
  } = searchTerm
    ? useGetExpenseTypeService({
        pageNumber: pageNo,
        size: pageSize,
        title: searchTerm,
      })
    : useGetExpenseTypeService({ pageNumber: pageNo, size: pageSize });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success)
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
    });
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, searchTerm]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const onEdit = (row: any) => {
    if (row.default === false) navigate("/expense-type/" + row.id);
  };
  const showRowView = (row: any) => {
    if (row.default === false) navigate("/expense-type/" + row.id);
  };
  const onDelete = (row: any) => {
    setDeleteModal(true);
    setRow(row);
  };
  const clearSearch = () => {
    setSearch("");
    setSearchTerm("");
    if (searchTerm) {
      handleSearch("");
    }
  };
  const handleSearch = (search: string) => {
    setSearchTerm(search);
    setPageNo(1);
    setPageSize(pageSize);
  };
  const handleSearchClick = () => {
    const trimmedSearch = search.trim();
    if (trimmedSearch !== "") {
      handleSearch(trimmedSearch);
    }
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value.trim());
    if (!event.target.value.trim()) handleSearch("");
  };
  const actionslist = [
    { label: "edit", onClick: onEdit, icon: <EditIcon /> },
    { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
  ];
  const adjustActions = (actions: any[]) => {
    return checkPrivileges("ROLE_EXPENSE_TYPE_UPDATE")
      ? checkPrivileges("ROLE_EXPENSE_TYPE_ARCHIVE")
        ? actions
        : actions.filter((action) => action.label !== "delete")
      : checkPrivileges("ROLE_EXPENSE_TYPE_ARCHIVE")
        ? actions.filter((action) => action.label !== "edit")
        : [];
  };

  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/expense-type/create")}
        heading={t("Expense Types")}
        icon={<LocalAtmIcon className="text-black w-[34px] h-[34px]" />}
        btnText={"create"}
        btnType={"create"}
        showCreateBtn={checkPrivileges("ROLE_EXPENSE_TYPE_CREATE")}
      />
      <Box>
        {isFetching || loading ? (
          <Loader />
        ) : (
          <div id="mainListingTable">
            <BackendPaginationTable
              columns={[
                { id: "title", label: "title", numeric: false, width: "55%" },
                {
                  id: "default",
                  label: "isDefault",
                  numeric: false,
                  width: "22%",
                },
                ...(adjustActions(actionslist).length
                  ? [
                      {
                        id: "actions",
                        label: "actions",
                        numeric: false,
                        width: "12%",
                      },
                    ]
                  : []),
              ]}
              data={tableData?.pages[0]?.data?.content || []}
              pageNo={pageNo}
              pageSize={pageSize}
              tableData={tableData?.pages[0]?.data || {}}
              showCheckbox={false}
              showActions={true}
              enableSorting={true}
              showHeader={true}
              showSearch={true}
              showFilter={true}
              showCreateButton={true}
              actions={adjustActions(actionslist)}
              handleChangePage={handleChangePage}
              handleChangeRows={handleChangeRows}
              rowClick={checkPrivileges("ROLE_EXPENSE_TYPE_UPDATE")}
              showRowView={showRowView}
              loadingSearch={false}
              // displayDefault={checkPrivileges('ROLE_MANAGE_DEFAULT_EXPENSE_TYPE')}
              headerComponent={
                <StyledInput
                  // fullWidth
                  placeholder={t("title")}
                  handleSearchClick={handleSearchClick}
                  handleChange={handleInputChange}
                  value={search}
                  clearSearch={clearSearch}
                />
              }
            />
          </div>
        )}
      </Box>
      {openDeleteModal && (
        <FormDialog
          open={openDeleteModal}
          onClose={() => setDeleteModal(false)}
          title="Delete Expense Type"
          // icon={<div><Home/></div>}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure you want to delete?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setLoading(true);
            setDeleteModal(false);
            ExpenseProfileService.deleteEntity(
              (row as any).id,
              "expensetype/archive",
            )
              .then((response: DeleteData) => {
                if (response.success) {
                  if (
                    tableData?.pages[0]?.data?.content?.length === 1 &&
                    pageNo > 1
                  )
                    setPageNo(1);
                  else refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("Expense Type") +
                        " " +
                        t("deleted") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="Delete"
        />
      )}
    </Box>
  );
}
